import { useQueryClient } from '@tanstack/react-query';
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { useGenericGet, useGenericPost, useGenericPostFormData } from './use-api.hook';
import { apiClient } from '../services/api-client.service';
import { useMemo } from 'react';
import { useActiveTenant } from '../states/active-tenant.atom';

export interface MediaFile {
  key: string;
  url: string;
  directoryUrl?: string;
  size: number;
  lastModified: string;
  isDirectory: boolean;
}

interface MediaResponse {
  success: boolean;
  data: MediaFile[];
}

export interface ValidationError {
  file: string;
  error: string;
}

export interface ZipUploadResponse {
  success: boolean;
  message: string;
  uploadedFiles?: MediaFile[];
  validationErrors?: ValidationError[];
  totalFiles?: number;
}

const useMediaBasePath = () => {
  const { activeTenant } = useActiveTenant();
  return useMemo(() => {
    return `/v1/admin/tenants/${activeTenant?.id}/media`;
  }, [activeTenant?.id]);
};

export const useMedia = () => {
  const basePath = useMediaBasePath();

  // List files and directories in a path
  const useListFiles = (path: string = '') => {
    const endpoint = path ? `${basePath}/${path}` : basePath;
    return useGenericGet<MediaResponse>(endpoint, ['media', 'list', path], apiClient, {
      queryKey: ['media', 'list', path],
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    });
  };

  // Upload files
  const useUploadFiles = (
    mutationOptions?: Omit<UseMutationOptions<MediaFile[], unknown, FormData>, 'mutationFn'>
  ) => {
    return useGenericPostFormData<MediaFile[]>(basePath, ['media'], apiClient, mutationOptions);
  };

  // Delete files
  const useDeleteFiles = (
    mutationOptions?: Omit<UseMutationOptions<void, unknown, { fileKeys: string[] }>, 'mutationFn'>
  ) => {
    return useGenericPost<void, { fileKeys: string[] }>(
      `${basePath}/delete`,
      ['media'],
      apiClient,
      mutationOptions
    );
  };

  // Create directory
  const useCreateDirectory = (
    mutationOptions?: Omit<UseMutationOptions<void, unknown, { path: string }>, 'mutationFn'>
  ) => {
    return useGenericPost<void, { path: string }>(
      `${basePath}/directory`,
      ['media'],
      apiClient,
      mutationOptions
    );
  };

  // Delete directory
  const useDeleteDirectory = (
    mutationOptions?: Omit<UseMutationOptions<void, unknown, { path: string }>, 'mutationFn'>
  ) => {
    return useGenericPost<void, { path: string }>(
      `${basePath}/delete-directory`,
      ['media'],
      apiClient,
      mutationOptions
    );
  };

  // Upload zip file
  const useUploadZip = (
    mutationOptions?: Omit<UseMutationOptions<ZipUploadResponse, unknown, FormData>, 'mutationFn'>
  ) => {
    return useGenericPostFormData<ZipUploadResponse>(
      `${basePath}/upload-zip`,
      ['media', 'zip'],
      apiClient,
      mutationOptions
    );
  };

  return {
    useListFiles,
    useUploadFiles,
    useDeleteFiles,
    useCreateDirectory,
    useDeleteDirectory,
    useUploadZip,
  };
};
